import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight, ArrowUpRight, ChevronLeft, ChevronRight,
  CheckCircle2,
} from "lucide-react";
import { COMPANY, PROJECTS } from "@/data/content";

// — Service category cards (5 tall portrait cards, EnergySage Home-3 pattern) —
const CATEGORIES = [
  {
    slug: "utility-scale-epc",
    title: "Utility Scale Solar EPC",
    tag: "EPC at giga-scale",
    blurb: "50 MW+ ground-mount EPC, evacuation design and grid synchronisation.",
    image: "https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    cta: "Explore EPC",
    to: "/services",
  },
  {
    slug: "pm-kusum",
    title: "PM-KUSUM",
    tag: "Distribution-tied solar",
    blurb: "Component-A & C developer for MNRE PM-KUSUM tenders.",
    image: "https://images.unsplash.com/photo-1629726797843-618688139f5a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    cta: "View PM-KUSUM",
    to: "/services",
  },
  {
    slug: "government-solar",
    title: "Government Solar",
    tag: "PSU & DISCOM tenders",
    blurb: "Empanelled execution partner for central & state agency projects.",
    image: "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    cta: "Government track record",
    to: "/government-projects",
  },
  {
    slug: "solar-parks",
    title: "Solar Parks",
    tag: "Park development",
    blurb: "Mode-5 / Mode-8 solar park development with land aggregation.",
    image: "https://images.pexels.com/photos/35501726/pexels-photo-35501726.jpeg?auto=compress&cs=tinysrgb&w=1600",
    cta: "Park development",
    to: "/services",
  },
  {
    slug: "open-access",
    title: "Open Access & Captive",
    tag: "Group captive structures",
    blurb: "Third-party sale and 26% group captive structures for HT consumers.",
    image: "https://images.unsplash.com/photo-1531834685032-c34bf0d84c77?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    cta: "Captive & open access",
    to: "/services",
  },
];

// — Split copy + media sections —
const SPLITS = [
  {
    overline: "Utility scale execution",
    title: "End-to-end EPC, from BoQ to grid synchronisation.",
    body: "VPCL Infra acts as a single-point EPC contractor for utility-scale ground-mount plants, including in-house pooling-substation engineering, bifacial + tracker integrations, and evacuation up to 400 kV.",
    cta: { label: "Explore our EPC capability", to: "/services" },
    image: "https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800",
    chip: "750+ MW developed",
  },
  {
    overline: "Government & utility partner",
    title: "Pre-qualified with India's largest DISCOMs and PSUs.",
    body: "From PM-KUSUM feeder solarisation to APDISCOM utility plants, VPCL Infra delivers government-anchored projects with audit-ready compliance and zero LD records.",
    cta: { label: "View government projects", to: "/government-projects" },
    image: "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800",
    chip: "60+ institutional clients",
  },
  {
    overline: "Landowner opportunities",
    title: "Land aggregation for the next generation of solar parks.",
    body: "Own 25+ acres near a substation? We engage with landowners through lease, joint-development and revenue-sharing structures — turning land into a long-term, predictable income stream.",
    cta: { label: "Submit land details", to: "/landowner" },
    image: "https://images.pexels.com/photos/35501726/pexels-photo-35501726.jpeg?auto=compress&cs=tinysrgb&w=1800",
    chip: "Lease | JD | Revenue Share",
  },
];

// — News / Insights cards —
const INSIGHTS = [
  {
    tag: "Case Study",
    title: "How VPCL delivered a 150 MW APDISCOM plant ahead of schedule",
    meta: "Anantapur, AP · Mar 2024 · 5 min read",
    image: "https://images.unsplash.com/photo-1629726797843-618688139f5a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    to: "/projects",
  },
  {
    tag: "Policy",
    title: "PM-KUSUM Component-C: an executor's view from 9 feeder sites",
    meta: "Telangana · 4 min read",
    image: "https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    to: "/projects",
  },
  {
    tag: "Open Access",
    title: "Group captive economics: tariff savings vs HT DISCOM rates",
    meta: "Gujarat · 6 min read",
    image: "https://images.pexels.com/photos/35501726/pexels-photo-35501726.jpeg?auto=compress&cs=tinysrgb&w=1400",
    to: "/projects",
  },
  {
    tag: "Industry",
    title: "Why 220 kV pooling substations are the new EPC differentiator",
    meta: "Industry view · 4 min read",
    image: "https://images.unsplash.com/photo-1531834685032-c34bf0d84c77?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    to: "/projects",
  },
];

const PRESS = ["Mint", "Economic Times", "Business Standard", "Hindu BusinessLine", "Reuters India"];
const PARTNERS = [
  "MNRE", "SECI", "APDISCOM", "TSSPDCL", "KSPDCL", "RVPN", "MSEDCL", "GETCO", "NTPC",
];

const BIG_STATS = [
  { value: "1,170 MW", label: "Developed + Under Execution" },
  { value: "60+", label: "Institutional clients across India" },
  { value: "14", label: "States with active project footprint" },
  { value: "Zero", label: "LD events on delivered EPC projects" },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* ===== HERO = EXPANDABLE CARD CAROUSEL (EnergySage Home-3 pattern) ===== */}
      <ServiceCarousel />

      {/* ===== SPLIT SECTIONS ===== */}
      {SPLITS.map((s, idx) => (
        <SplitSection key={idx} idx={idx} reverse={idx % 2 === 1} {...s} />
      ))}

      {/* ===== UNMATCHED EXPERIENCE — center CTA banner ===== */}
      <section className="bg-[#F8F9FA] section-padding" data-testid="experience-banner">
        <div className="container-corp text-center max-w-3xl mx-auto">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#0B2341] leading-tight">
            Investor-grade execution. Government-grade compliance.
          </h2>
          <p className="mt-6 text-[#4A5568] leading-relaxed">
            A disciplined development desk, in-house engineering and HSE-first culture — delivered across 14 states.
          </p>
          <div className="mt-10">
            <Link to="/contact" className="btn-accent group" data-testid="experience-cta">
              Get started
              <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== INSIGHTS / NEWS GRID ===== */}
      <section className="section-padding bg-white" data-testid="insights-section">
        <div className="container-corp">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
            <div className="max-w-2xl">
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#0B2341] leading-tight">
                Renewable infrastructure, in our own words.
              </h2>
            </div>
            <Link to="/projects" className="text-sm font-medium text-[#0B2341] hover:text-[#16A34A] inline-flex items-center gap-2 group" data-testid="all-insights">
              Explore all projects
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          <div className="grid lg:grid-cols-12 gap-6">
            {/* Big feature */}
            <Link
              to={INSIGHTS[0].to}
              data-testid="insight-feature"
              className="lg:col-span-7 group block"
            >
              <div className="relative aspect-[16/10] overflow-hidden mb-5">
                <img src={INSIGHTS[0].image} alt={INSIGHTS[0].title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#16A34A] mb-3">{INSIGHTS[0].tag}</div>
              <h3 className="font-heading text-2xl lg:text-3xl font-semibold text-[#0B2341] leading-tight group-hover:text-[#16A34A] transition-colors max-w-xl">
                {INSIGHTS[0].title}
              </h3>
              <div className="mt-3 text-xs text-[#4A5568]">{INSIGHTS[0].meta}</div>
            </Link>

            {/* Side stack */}
            <div className="lg:col-span-5 flex flex-col gap-6">
              {INSIGHTS.slice(1).map((p, i) => (
                <Link to={p.to} key={i} data-testid={`insight-${i}`} className="group grid grid-cols-12 gap-4">
                  <div className="col-span-5 relative aspect-[4/3] overflow-hidden">
                    <img src={p.image} alt={p.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="col-span-7 flex flex-col justify-center">
                    <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#16A34A] mb-1.5">{p.tag}</div>
                    <h4 className="font-heading text-base font-semibold text-[#0B2341] leading-snug group-hover:text-[#16A34A] transition-colors">
                      {p.title}
                    </h4>
                    <div className="mt-2 text-[11px] text-[#4A5568]">{p.meta}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== ZIP / CAPTURE FORM ===== */}
      <CaptureBanner />

      {/* ===== PRESS / FEATURED-IN ===== */}
      <section className="bg-white border-y border-[#E2E8F0] py-12 overflow-hidden" data-testid="press-section">
        <div className="container-corp">
          <div className="text-center text-[10px] uppercase tracking-[0.28em] font-bold text-[#4A5568] mb-8">
            As featured in
          </div>
          <div className="relative overflow-hidden">
            <div className="flex items-center gap-16 animate-marquee whitespace-nowrap">
              {[...PRESS, ...PRESS, ...PRESS].map((p, i) => (
                <span key={i} className="font-heading text-2xl lg:text-3xl font-light text-[#0B2341]/40 hover:text-[#0B2341] transition-colors tracking-tight">
                  {p}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== BIG STATS WITH BACKGROUND ===== */}
      <section className="relative bg-[#0B2341] text-white overflow-hidden" data-testid="big-stats-section">
        <img
          src="https://images.pexels.com/photos/35501726/pexels-photo-35501726.jpeg?auto=compress&cs=tinysrgb&w=2400"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B2341] via-[#0B2341]/85 to-[#061426]" />
        <div className="relative container-corp py-24 md:py-32">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight">
              Over 60 institutional clients trust VPCL Infra with their renewable assets.
            </h2>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-12 gap-x-10">
            {BIG_STATS.map((s, i) => (
              <div key={i} data-testid={`big-stat-${i}`} className="text-center lg:text-left">
                <div className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white">
                  {s.value}
                </div>
                <div className="mt-3 text-xs uppercase tracking-[0.22em] text-[#16A34A] font-bold">
                  {s.label}
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-16">
            <Link to="/about" className="inline-flex items-center gap-2 text-sm font-medium text-white border-b-2 border-[#16A34A] pb-1 hover:text-[#16A34A] transition-colors" data-testid="mission-link">
              Our mission and values
              <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== TRUSTED BY PARTNERS ===== */}
      <section className="section-padding bg-white" data-testid="trusted-by-section">
        <div className="container-corp">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <h2 className="font-heading text-4xl sm:text-5xl font-semibold text-[#0B2341] leading-tight tracking-tight">
                Trusted by
              </h2>
              <p className="mt-6 text-[#4A5568] leading-relaxed max-w-md">
                We partner with central PSUs, state DISCOMs, nodal agencies and infrastructure developers to deliver India&apos;s renewable transition.
              </p>
              <Link to="/contact" className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-[#0B2341] border-b-2 border-[#16A34A] pb-1 hover:text-[#16A34A] transition-colors" data-testid="partner-cta">
                Partner with us
                <ArrowUpRight size={14} />
              </Link>
            </div>
            <div className="lg:col-span-7">
              <div className="grid grid-cols-3 sm:grid-cols-3 gap-px bg-[#E2E8F0]">
                {PARTNERS.map((p) => (
                  <div key={p} data-testid={`partner-${p}`} className="bg-white aspect-[5/3] flex items-center justify-center font-heading text-base sm:text-lg font-semibold text-[#0B2341]/55 hover:text-[#0B2341] transition-colors px-4 text-center">
                    {p}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== MAKE AN IMPACT — closer ===== */}
      <section className="bg-[#061426] text-white" data-testid="impact-closer">
        <div className="container-corp py-20 md:py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight">
              Make an impact.<br />
              <span className="text-[#16A34A]">It&apos;s never been easier.</span>
            </div>
          </div>
          <div className="lg:col-span-5">
            <p className="text-white/70 leading-relaxed mb-8 max-w-md">
              From a 50 MW PM-KUSUM cluster to a 400 MW utility plant, our team is built to deliver India&apos;s next decade of renewable infrastructure.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact" className="btn-accent" data-testid="impact-cta-primary">
                Discuss your project
                <ArrowRight size={16} className="ml-2" />
              </Link>
              <Link to="/contact" className="btn-ghost-light" data-testid="impact-cta-secondary">
                Download profile
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ============= COMPONENTS ============= */

function ServiceCarousel() {
  const [active, setActive] = useState(0);
  const total = CATEGORIES.length;

  const prev = () => setActive((a) => (a - 1 + total) % total);
  const next = () => setActive((a) => (a + 1) % total);

  return (
    <section className="bg-white pt-10 md:pt-14 pb-16 md:pb-24" data-testid="hero-section">
      <div className="container-corp">
        {/* Hero headline above expandable cards (EnergySage pattern) */}
        <div className="grid lg:grid-cols-12 gap-6 lg:gap-10 items-end mb-10 md:mb-14">
          <div className="lg:col-span-9">
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-[88px] font-semibold text-[#0B2341] leading-[0.98] tracking-[-0.02em]">
              Power India&apos;s grid,<br />
              <span className="text-[#16A34A]">your way.</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-[#4A5568] leading-relaxed max-w-2xl">
              Utility Scale Solar · PM-KUSUM · Government PPA · Open Access · Solar EPC. Hover any tile to expand — the renewable infrastructure you need, delivered end-to-end.
            </p>
          </div>
          <div className="hidden md:flex lg:col-span-3 lg:justify-end items-center gap-2">
            <button onClick={prev} data-testid="carousel-prev" aria-label="Previous" className="w-11 h-11 border border-[#E2E8F0] text-[#0B2341] hover:bg-[#0B2341] hover:text-white transition-colors flex items-center justify-center">
              <ChevronLeft size={18} />
            </button>
            <button onClick={next} data-testid="carousel-next" aria-label="Next" className="w-11 h-11 border border-[#E2E8F0] text-[#0B2341] hover:bg-[#0B2341] hover:text-white transition-colors flex items-center justify-center">
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        {/* Expandable card row — flex with grow on hover */}
        <div className="hidden md:flex gap-3 h-[600px]">
          {CATEGORIES.map((c, i) => (
            <Link
              to={c.to}
              key={c.slug}
              data-testid={`category-${c.slug}`}
              onMouseEnter={() => setActive(i)}
              className={`group relative overflow-hidden block transition-all duration-[600ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
                active === i ? "flex-[3.2]" : "flex-[1]"
              }`}
            >
              <img
                src={c.image}
                alt={c.title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
              />
              <div className={`absolute inset-0 transition-all duration-500 ${
                active === i
                  ? "bg-gradient-to-t from-[#0B2341] via-[#0B2341]/55 to-[#0B2341]/10"
                  : "bg-gradient-to-t from-[#0B2341]/95 via-[#0B2341]/70 to-[#0B2341]/30"
              }`} />
              <div className="absolute inset-0 p-6 lg:p-8 flex flex-col justify-between text-white">
                <div className="flex justify-between items-start">
                  <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#16A34A]">
                    {String(i + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
                  </div>
                  <ArrowUpRight size={18} className={`transition-opacity duration-300 ${active === i ? "opacity-100" : "opacity-0"}`} />
                </div>
                {/* Compact label rotated when collapsed */}
                <div className={`absolute left-0 bottom-0 right-0 p-6 lg:p-8 transition-all duration-500 ${
                  active === i ? "opacity-0 pointer-events-none" : "opacity-100"
                }`}>
                  <div
                    className="font-heading font-semibold text-lg lg:text-xl whitespace-nowrap"
                    style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
                  >
                    {c.title}
                  </div>
                </div>
                {/* Expanded content */}
                <div className={`transition-all duration-500 ${
                  active === i ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                }`}>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#16A34A] font-bold mb-3">
                    {c.tag}
                  </div>
                  <h3 className="font-heading font-semibold leading-tight mb-3 text-3xl lg:text-4xl max-w-md">
                    {c.title}
                  </h3>
                  <p className="text-sm lg:text-base text-white/80 leading-relaxed mb-5 max-w-md">
                    {c.blurb}
                  </p>
                  <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-medium text-white border-b-2 border-[#16A34A] pb-1">
                    {c.cta} <ArrowRight size={12} />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Mobile stack */}
        <div className="md:hidden space-y-3">
          {CATEGORIES.map((c, i) => (
            <Link
              to={c.to}
              key={c.slug}
              data-testid={`category-mobile-${c.slug}`}
              className="relative overflow-hidden block aspect-[16/10]"
            >
              <img src={c.image} alt={c.title} className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B2341] via-[#0B2341]/55 to-transparent" />
              <div className="absolute inset-0 p-5 flex flex-col justify-end text-white">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#16A34A] font-bold mb-2">
                  {c.tag}
                </div>
                <h3 className="font-heading text-xl font-semibold mb-2">{c.title}</h3>
                <p className="text-sm text-white/80 leading-relaxed">{c.blurb}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function SplitSection({ title, body, cta, image, chip, reverse, idx }) {
  return (
    <section className="section-padding bg-white" data-testid={`split-section-${idx}`}>
      <div className="container-corp">
        <div className={`grid lg:grid-cols-12 gap-12 lg:gap-16 items-center ${reverse ? "lg:[&>div:first-child]:order-2" : ""}`}>
          <div className="lg:col-span-6">
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#0B2341] leading-[1.1] tracking-tight max-w-xl">
              {title}
            </h2>
            <p className="mt-6 text-[#4A5568] leading-relaxed max-w-lg text-base">
              {body}
            </p>
            <Link to={cta.to} className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-[#0B2341] border-b-2 border-[#16A34A] pb-1 hover:text-[#16A34A] transition-colors" data-testid={`split-cta-${idx}`}>
              {cta.label}
              <ArrowUpRight size={14} />
            </Link>
          </div>
          <div className="lg:col-span-6">
            <div className="relative aspect-[4/3] overflow-hidden">
              <img src={image} alt="" className="absolute inset-0 w-full h-full object-cover" />
              {chip && (
                <div className="absolute top-5 left-5 bg-white px-4 py-2 text-xs uppercase tracking-[0.18em] font-bold text-[#0B2341] border-l-4 border-[#16A34A] shadow-lg">
                  {chip}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CaptureBanner() {
  const [state, setState] = useState("");
  const [capacity, setCapacity] = useState("");

  const submit = (e) => {
    e.preventDefault();
    if (!state) { toast.error("Please enter your state."); return; }
    toast.success(`Thanks — our development desk will reach out about ${capacity || "your"} project in ${state}.`);
    setState("");
    setCapacity("");
  };

  return (
    <section className="bg-[#F8F9FA] section-padding" data-testid="capture-banner">
      <div className="container-corp max-w-3xl mx-auto text-center">
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#0B2341] leading-tight">
          Start your renewable project with <span className="text-[#16A34A]">VPCL Infra</span>
        </h2>
        <p className="mt-5 text-[#4A5568] leading-relaxed">
          Tell us where you operate and we&apos;ll route you to the right development partner within 48 hours.
        </p>
        <form onSubmit={submit} className="mt-10 flex flex-col sm:flex-row gap-3 max-w-2xl mx-auto" data-testid="capture-form">
          <input
            type="text"
            value={state}
            onChange={(e) => setState(e.target.value)}
            placeholder="State (e.g. Telangana)"
            className="flex-1 bg-white border border-[#E2E8F0] focus:border-[#0B2341] focus:ring-1 focus:ring-[#0B2341] focus:outline-none px-5 py-4 text-sm text-[#0A1118] placeholder:text-[#4A5568]/70 rounded-none"
            data-testid="capture-state"
          />
          <input
            type="text"
            value={capacity}
            onChange={(e) => setCapacity(e.target.value)}
            placeholder="Capacity (MW)"
            className="sm:max-w-[180px] bg-white border border-[#E2E8F0] focus:border-[#0B2341] focus:ring-1 focus:ring-[#0B2341] focus:outline-none px-5 py-4 text-sm text-[#0A1118] placeholder:text-[#4A5568]/70 rounded-none"
            data-testid="capture-capacity"
          />
          <button type="submit" className="btn-accent group whitespace-nowrap" data-testid="capture-submit">
            Engage VPCL
            <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
          </button>
        </form>
        <p className="mt-4 text-[11px] text-[#4A5568]/80">
          Your information is treated as confidential. <Link to="/contact" className="text-[#16A34A] hover:underline">Privacy</Link>
        </p>
      </div>
    </section>
  );
}
